import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useComplianceUsers, useCreateAudit } from "@/hooks/use-audits";
import { useAllProjects, useProject } from "@/hooks/use-projects";
import { useTemplates } from "@/hooks/use-templates";

interface CreateAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultProjectId?: string;
}

const schema = z.object({
  projectId: z.string().min(1, "Project is required"),
  templateIds: z.array(z.string()).min(1, "At least one template is required"),
  assignedToId: z.string().min(1, "Assignee is required"),
});

type FormValues = z.infer<typeof schema>;

export function CreateAuditDialog({
  open,
  onOpenChange,
  defaultProjectId,
}: CreateAuditDialogProps) {
  const { data: projects } = useAllProjects();
  const { data: selectedProject } = useProject(defaultProjectId ?? "");
  const { data: templates } = useTemplates({ status: "active" });
  const { data: complianceUsers } = useComplianceUsers();
  const createAudit = useCreateAudit();

  const auditors = (complianceUsers ?? []).filter(
    (u) => u.roles?.includes("compliance_auditor") || u.role === "compliance_auditor",
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      projectId: defaultProjectId ?? "",
      templateIds: [],
      assignedToId: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        projectId: defaultProjectId ?? "",
        templateIds: [],
        assignedToId: "",
      });
    }
  }, [open, defaultProjectId, form]);

  async function onSubmit(values: FormValues) {
    const project = (projects ?? []).find((p) => p.id === values.projectId);
    await createAudit.mutateAsync({
      projectId: values.projectId,
      projectName: project?.name ?? "",
      templateIds: values.templateIds,
      assignedToId: values.assignedToId,
    });
    form.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Audit</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {defaultProjectId ? (
              <div className="space-y-1">
                <p className="text-sm font-medium leading-none">Project</p>
                <p className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                  {selectedProject?.name ?? defaultProjectId}
                </p>
              </div>
            ) : (
              <FormField
                control={form.control}
                name="projectId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a project" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(projects ?? []).map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="templateIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Templates</FormLabel>
                  <FormControl>
                    <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-input p-3">
                      {(templates ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No active templates</p>
                      ) : (
                        (templates ?? []).map((t) => (
                          <div key={t.id} className="flex items-center gap-3">
                            <Checkbox
                              id={`create-audit-template-${t.id}`}
                              checked={field.value.includes(t.id)}
                              onCheckedChange={(checked) => {
                                field.onChange(
                                  checked
                                    ? [...field.value, t.id]
                                    : field.value.filter((id) => id !== t.id),
                                );
                              }}
                            />
                            <Label
                              htmlFor={`create-audit-template-${t.id}`}
                              className="cursor-pointer font-normal"
                            >
                              {t.name}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assignedToId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign To</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an auditor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {auditors.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createAudit.isPending}>
                {createAudit.isPending ? "Creating…" : "Create Audit"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
